import { AlertCircle, CheckCircle2, Clock, Plus } from 'lucide-react';
import { useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import {
  useAddPerfectionStep,
  useCompletePerfectionStep,
  useOutstandingPerfection,
  usePerfectionSummary,
} from '@/hooks/use-perfection';
import { fmtDate as formatDate } from '@/lib/format';
import { toast } from '@/stores/toast-store';

interface PerfectionTabProps {
  loanId: string;
}

const PERFECTION_STEP_TYPES = [
  { value: 'MORTGAGE_REGISTRATION', label: 'Mortgage Registration' },
  { value: 'CAVEAT_LODGEMENT', label: 'Caveat Lodgement' },
  { value: 'PPSR_REGISTRATION', label: 'PPSR Registration' },
  { value: 'TITLE_SEARCH', label: 'Title Search' },
  { value: 'DISCHARGE_REGISTRATION', label: 'Discharge Registration' },
  { value: 'INSURANCE_ASSIGNMENT', label: 'Insurance Assignment' },
  { value: 'DIRECTORS_GUARANTEE', label: 'Directors Guarantee' },
  { value: 'OTHER', label: 'Other' },
];

export function PerfectionTab({ loanId }: PerfectionTabProps) {
  const { data: summary, isLoading: summaryLoading } = usePerfectionSummary(loanId);
  const { data: outstanding } = useOutstandingPerfection(loanId);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedSecurityId, setSelectedSecurityId] = useState<string>('');
  const [formData, setFormData] = useState({ type: '', notes: '' });
  const [completingStepId, setCompletingStepId] = useState<string>('');
  const [referenceNumber, setReferenceNumber] = useState('');

  const addStep = useAddPerfectionStep();
  const completeStep = useCompletePerfectionStep();

  const handleAddStep = async () => {
    if (!selectedSecurityId || !formData.type) {
      toast.error('Please fill in required fields');
      return;
    }

    addStep.mutate(
      {
        securityId: selectedSecurityId,
        stepType: formData.type,
        notes: formData.notes,
      },
      {
        onSuccess: () => {
          toast.success('Perfection step added');
          setFormData({ type: '', notes: '' });
          setShowAddForm(false);
        },
      },
    );
  };

  const handleCompleteStep = (stepId: string) => {
    if (!referenceNumber) {
      toast.error('Please enter reference number');
      return;
    }

    completeStep.mutate(
      { id: stepId, referenceNo: referenceNumber },
      {
        onSuccess: () => {
          toast.success('Step completed');
          setCompletingStepId('');
          setReferenceNumber('');
        },
      },
    );
  };

  if (summaryLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" />
        Loading perfection data…
      </div>
    );
  }

  const summaryRows = Array.isArray(summary) ? summary : (summary?.securities ?? []);
  const securities = summaryRows.map((security: any) => ({
    id: security.id ?? security.securityId,
    description: security.description,
    type: security.type ?? security.securityType,
    steps: security.steps ?? security.perfectionSteps ?? [],
    totalSteps: security.totalSteps ?? (security.steps ?? security.perfectionSteps ?? []).length,
    completed: security.completed ?? 0,
  }));
  const totalSteps = securities.reduce(
    (total: number, security: any) => total + security.totalSteps,
    0,
  );
  const completedSteps = securities.reduce(
    (total: number, security: any) => total + security.completed,
    0,
  );
  const progressPercent = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

  const outstandingCount = Array.isArray(outstanding)
    ? outstanding.length
    : (outstanding?.count ?? 0);

  return (
    <div className="space-y-6">
      {/* Warning banner */}
      {outstandingCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-amber-900">Outstanding Perfection Steps</h3>
            <p className="text-sm text-amber-700 mt-1">
              {outstandingCount} step{outstandingCount !== 1 ? 's' : ''} across your loans require
              attention
            </p>
          </div>
        </div>
      )}

      {/* Overall progress */}
      {totalSteps > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-900">Overall Progress</p>
            <p className="text-sm font-medium text-gray-600">
              {completedSteps}/{totalSteps}
            </p>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-indigo-600 transition-all"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* Securities sections */}
      {securities.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400 bg-white border border-gray-200 rounded-lg">
          <AlertCircle className="w-8 h-8 mb-2" />
          <p className="text-sm">No securities on this loan</p>
        </div>
      ) : (
        <div className="space-y-4">
          {securities.map((security: any) => (
            <div
              key={security.id}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden"
            >
              {/* Security header */}
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <p className="font-medium text-gray-900">{security.description || 'Security'}</p>
                <p className="text-xs text-gray-500 mt-0.5">{security.type}</p>
              </div>

              {/* Steps */}
              <div className="p-4 space-y-3">
                {security.steps.length === 0 ? (
                  <p className="text-sm text-gray-400">No perfection steps</p>
                ) : (
                  <>
                    {security.steps.map((step: any) => {
                      const isCompleted = step.status === 'COMPLETED';
                      const isFailed = step.status === 'FAILED';
                      const isPending = step.status === 'PENDING';
                      const isInProgress = step.status === 'IN_PROGRESS';
                      const stepType = step.type ?? step.stepType;
                      const referenceNo = step.referenceNumber ?? step.referenceNo;
                      const completedAt = step.completedAt ?? step.completedDate;

                      return (
                        <div
                          key={step.id}
                          className="flex items-start gap-3 p-3 rounded-md border border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          {isCompleted && (
                            <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                          )}
                          {isFailed && (
                            <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                          )}
                          {(isPending || isInProgress) && (
                            <Clock className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                          )}

                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm">{stepType}</p>
                            {referenceNo && (
                              <p className="text-xs text-gray-500">Ref: {referenceNo}</p>
                            )}
                            {step.notes && (
                              <p className="text-xs text-gray-600 mt-1">{step.notes}</p>
                            )}
                            <div className="flex gap-3 text-xs text-gray-400 mt-2">
                              {step.createdAt && <span>Created: {formatDate(step.createdAt)}</span>}
                              {completedAt && <span>Completed: {formatDate(completedAt)}</span>}
                            </div>
                          </div>

                          {(isPending || isInProgress) && !completingStepId && (
                            <button
                              onClick={() => setCompletingStepId(step.id)}
                              className="px-2 py-1 text-xs font-medium text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors shrink-0"
                            >
                              Complete
                            </button>
                          )}

                          {completingStepId === step.id && (
                            <div className="flex gap-1">
                              <input
                                type="text"
                                value={referenceNumber}
                                onChange={e => setReferenceNumber(e.target.value)}
                                placeholder="Ref #"
                                className="text-xs px-2 py-1 border border-gray-200 rounded"
                              />
                              <button
                                onClick={() => handleCompleteStep(step.id)}
                                disabled={completeStep.isPending}
                                className="px-2 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors disabled:opacity-50"
                              >
                                Save
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}

                {/* Add step button */}
                {!showAddForm && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setSelectedSecurityId(security.id);
                      setShowAddForm(true);
                    }}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-600 hover:text-blue-700 hover: rounded transition-colors w-full justify-center"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add Step
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Step Form */}
      {showAddForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Step Type</label>
            <select
              value={formData.type}
              onChange={e => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select type</option>
              {PERFECTION_STEP_TYPES.map(t => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={e => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Add notes..."
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              variant="secondary"
              onClick={() => {
                setShowAddForm(false);
                setFormData({ type: '', notes: '' });
              }}
              className="px-3 py-1.5 border border-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAddStep}
              disabled={addStep.isPending}
              className="px-3 py-1.5 text-white rounded-md text-sm font-medium hover: transition-colors disabled:opacity-50"
            >
              Add Step
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
